import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import PriceUpdateModal from "@/components/PriceUpdateModal";
import { Plus, PencilSimple, Trash, Pill, ChartLine, Package as PackageIcon, ListChecks, Warning, Drop, TrendUp, CurrencyCircleDollar, ShoppingCart, Archive, ArrowCounterClockwise, UploadSimple, X, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { USAGE_UNITS, CATEGORIES, EMPTY, DOSE_UNITS_FOR, expiryBadge, EMPTY_BULL, EMPTY_PROTOCOL, muid } from "./constants";

export function MedCounts() {
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ current_quantity: 0, add_purchase: 0, date: new Date().toISOString().slice(0, 10), notes: "" });

  const load = async () => {
    const [med, hist] = await Promise.all([
      api.get("/medicines"),
      api.get("/medicine-counts?limit=200"),
    ]);
    setItems(med.data);
    setHistory(hist.data);
  };
  useEffect(() => { load(); }, []);

  const startCount = (m) => {
    setSelected(m);
    setForm({ current_quantity: m.stock, add_purchase: 0, date: new Date().toISOString().slice(0, 10), notes: "" });
  };
  const cancel = () => setSelected(null);

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      current_quantity: Number(form.current_quantity),
      add_purchase: Number(form.add_purchase),
      date: form.date,
      notes: form.notes,
    };
    try {
      const res = await api.post(`/medicines/${selected.id}/count`, body);
      const consumed = res.data.consumed_quantity;
      const cost = res.data.cost;
      if (consumed > 0) toast.success(`Sayım kaydedildi · ${consumed} ${selected.usage_unit} tüketildi (${cost.toLocaleString("tr-TR")} ₺)`);
      else toast.success("Sayım kaydedildi · tüketim yok");
      setSelected(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const prev = Number(selected?.stock || 0);
  const added = Number(form.add_purchase || 0);
  const current = Number(form.current_quantity || 0);
  const consumed = Math.max(0, prev + added - current);
  const diff = current - (prev + added);

  const overdue = items.filter(x => x.reminder_status === "gecikti").length;
  const dueSoon = items.filter(x => x.reminder_status === "yaklaştı" || x.reminder_status === "hiç sayım yok").length;

  return (
    <div className="space-y-6">
      {(overdue > 0 || dueSoon > 0) && (
        <Card className="!bg-[#fbeee9] border-[#f2d4cb]" data-testid="med-count-warning">
          <div className="flex items-center gap-3">
            <Warning size={22} weight="fill" className="text-[#C45B45] shrink-0" />
            <div className="text-sm text-[#8a3a2b]">
              <b>{overdue}</b> ilaç sayımı gecikti, <b>{dueSoon}</b> ilaç yaklaştı veya hiç sayılmadı. Aylık sayım için aşağıdan başlayın.
            </div>
          </div>
        </Card>
      )}

      {selected ? (
        <Card>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="heading text-lg">{selected.name} — Aylık Sayım</h3>
              <div className="text-xs text-[#6b746c] mt-0.5">Sistemde kayıtlı miktar: <b>{prev} {selected.usage_unit}</b></div>
            </div>
            <Button onClick={cancel} className="!bg-transparent !text-[#6b746c] border border-[#E5E2DC]">İptal</Button>
          </div>
          <form onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <Field label={`Fiziki Sayım (${selected.usage_unit}) *`}>
              <Input data-testid="med-count-current" type="number" step="0.01" required
                value={form.current_quantity} onChange={e => setForm({ ...form, current_quantity: e.target.value })} />
            </Field>
            <Field label={`Sayım Öncesi Alınan (${selected.usage_unit})`}>
              <Input data-testid="med-count-added" type="number" step="0.01"
                value={form.add_purchase} onChange={e => setForm({ ...form, add_purchase: e.target.value })} />
            </Field>
            <Field label="Sayım Tarihi *">
              <Input data-testid="med-count-date" type="date" required
                value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </Field>
            <Field label="Notlar">
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Örn. bozulan/dökülen" />
            </Field>
            <div className="md:col-span-2">
              <div className={`text-sm px-3 py-3 rounded-md border ${
                diff > 0 ? "bg-[#F5E9D3] border-[#e0c890] text-[#8B6914]"
                : consumed > 0 ? "bg-[#E4EEE6] border-[#c2d6c8] text-[#2D4A36]"
                : "bg-[#F5F2EC] border-[#E5E2DC] text-[#6b746c]"
              }`} data-testid="med-count-preview">
                {diff > 0 ? (
                  <>⚠️ Fiziki sayım, sistem + alınan miktardan <b>{diff.toFixed(2)} {selected.usage_unit}</b> fazla. Bu miktar sisteme <b>eklendi</b> olarak işlenecek.</>
                ) : consumed > 0 ? (
                  <>Tüketim: <b>{consumed.toFixed(2)} {selected.usage_unit}</b> · Maliyet: <b>{(consumed * Number(selected.unit_price || 0)).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ₺</b>
                  <br /><span className="text-xs">Bu tutar Finans'a otomatik gider olarak eklenecek</span></>
                ) : (
                  <>Tüketim yok, sistem stok değeri güncellenecek: <b>{current} {selected.usage_unit}</b></>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <Button data-testid="med-count-submit" type="submit"><Plus size={14} className="inline mr-1" /> Sayımı Kaydet</Button>
            </div>
          </form>
        </Card>
      ) : (
        <Card className="!p-0 overflow-hidden">
          <table className="data-table">
            <thead><tr>
              <th>İlaç</th><th>Kategori</th>
              <th className="text-right">Sistem Stoğu</th>
              <th className="text-right">Son Sayım</th>
              <th>Durum</th><th></th>
            </tr></thead>
            <tbody>
              {items.map(m => (
                <tr key={m.id} data-testid={`med-count-row-${m.id}`}>
                  <td className="font-semibold">{m.name}</td>
                  <td className="text-[#6b746c] capitalize">{m.category || "—"}</td>
                  <td className="text-right tabnum">{m.stock} {m.usage_unit}</td>
                  <td className="text-right text-xs text-[#6b746c]">
                    {m.last_count_date ? `${m.last_count_date} (${m.days_since_count}g)` : "—"}
                  </td>
                  <td>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      m.reminder_status === "gecikti" || m.reminder_status === "hiç sayım yok" ? "bg-[#fbeee9] text-[#8a3a2b]"
                      : m.reminder_status === "yaklaştı" ? "bg-[#F5E9D3] text-[#8B6914]"
                      : "bg-[#E4EEE6] text-[#2D4A36]"
                    }`}>{m.reminder_status}</span>
                  </td>
                  <td>
                    <Button onClick={() => startCount(m)} data-testid={`med-count-start-${m.id}`}
                      className="!py-1 !px-3 text-xs">Sayım Yap</Button>
                  </td>
                </tr>
              ))}
              {!items.length && <tr><td colSpan={6} className="text-center py-8 text-[#8a938c]">Henüz ilaç kayıtlı değil</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      {history.length > 0 && (
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-[#E5E2DC]">
            <h3 className="heading text-lg">Sayım Geçmişi</h3>
          </div>
          <table className="data-table">
            <thead><tr>
              <th>Tarih</th><th>İlaç</th>
              <th className="text-right">Önceki</th>
              <th className="text-right">Alınan</th>
              <th className="text-right">Şimdi</th>
              <th className="text-right">Tüketim</th>
              <th className="text-right">Maliyet</th>
            </tr></thead>
            <tbody>
              {history.slice(0, 30).map(h => (
                <tr key={h.id} data-testid={`med-count-hist-${h.id}`}>
                  <td className="tabnum">{h.date}</td>
                  <td className="font-semibold">{h.medicine_name}</td>
                  <td className="text-right tabnum text-[#6b746c]">{h.previous_quantity}</td>
                  <td className="text-right tabnum text-[#2D4A36]">{h.added_purchase || 0}</td>
                  <td className="text-right tabnum font-semibold">{h.current_quantity}</td>
                  <td className="text-right tabnum text-[#C45B45]">{h.consumed_quantity}</td>
                  <td className="text-right tabnum">{Number(h.cost || 0).toLocaleString("tr-TR")} ₺</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}


// -----------------------------------------------------------
// SEMEN STOCK — Sperma / Boğa Payet Stok Yönetimi
// -----------------------------------------------------------
